<template>
  <v-dialog v-model="dialog" max-width="1100px" scrollable>
    <v-card>
      <v-toolbar density="compact" color="teal-darken-2" dark>
        <v-toolbar-title class="text-subtitle-1">
          <v-icon size="small">mdi-currency-usd</v-icon>
          {{ $t('projects.cost.title', { id: projectId }) }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon variant="text" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <div v-if="loading" class="d-flex align-center py-6">
          <v-progress-circular indeterminate size="24" class="mr-3" />
          {{ $t('common.loading') }}
        </div>

        <v-alert v-else-if="error" type="error" variant="tonal" density="comfortable">
          {{ error }}
        </v-alert>

        <template v-else-if="report">
          <!-- Totale del processo -->
          <div class="d-flex align-center flex-wrap mb-4">
            <div class="mr-8">
              <div class="text-caption text-medium-emphasis">{{ $t('projects.cost.total') }}</div>
              <div class="text-h4">{{ fmtUsd(report.totals.costUsd) }}</div>
            </div>
            <div class="mr-8">
              <div class="text-caption text-medium-emphasis">{{ $t('projects.cost.runs') }}</div>
              <div class="text-h6">{{ report.totals.runs }}</div>
            </div>
            <div class="mr-8">
              <div class="text-caption text-medium-emphasis">{{ $t('projects.cost.failures') }}</div>
              <div class="text-h6">{{ report.totals.failedRuns }}</div>
            </div>
            <div>
              <div class="text-caption text-medium-emphasis">{{ $t('projects.cost.tokens') }}</div>
              <div class="text-h6">
                {{ fmtNum(report.totals.inputTokens) }} / {{ fmtNum(report.totals.outputTokens) }}
              </div>
            </div>
          </div>

          <!-- Perché un totale può essere più basso del vero: va detto, non nascosto -->
          <v-alert
            v-if="report.totals.legacyRuns"
            type="info" variant="tonal" density="compact" class="mb-3"
          >
            {{ $t('projects.cost.legacyHint', { n: report.totals.legacyRuns }) }}
          </v-alert>
          <v-alert
            v-if="report.totals.unpricedRuns"
            type="warning" variant="tonal" density="compact" class="mb-3"
          >
            {{ $t('projects.cost.unpricedHint', { n: report.totals.unpricedRuns }) }}
          </v-alert>
          <v-alert
            v-if="report.totals.partialRuns"
            type="warning" variant="tonal" density="compact" class="mb-3"
          >
            {{ $t('projects.cost.partialHint', { n: report.totals.partialRuns }) }}
          </v-alert>

          <v-data-table
            v-if="report.byVerification.length"
            :headers="headers"
            :items="report.byVerification"
            :items-per-page="25"
            density="compact"
            class="elevation-1"
          />
          <v-alert v-else type="info" variant="tonal" density="comfortable">
            {{ $t('projects.cost.noData') }}
          </v-alert>

          <div class="text-caption text-medium-emphasis mt-3">
            {{ $t('projects.cost.hint') }}
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiCall } from '../utils/api.js'
import { formatUsd, formatUsdShort } from '../utils/money.js'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  projectId: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

const { t } = useI18n()

const dialog = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})

const report = ref(null)
const loading = ref(false)
const error = ref('')

const fmtUsd = formatUsdShort
const fmtNum = (n) => Number(n || 0).toLocaleString()

const headers = computed(() => [
  { title: t('projects.cost.verification'), key: 'verificationId' },
  { title: t('projects.cost.step'), key: 'stepId' },
  { title: t('projects.cost.engine'), key: 'engine' },
  { title: t('projects.cost.model'), key: 'model' },
  { title: t('projects.cost.runs'), key: 'totals.runs', align: 'end' },
  { title: t('projects.cost.failures'), key: 'totals.failedRuns', align: 'end' },
  {
    title: t('projects.cost.costColumn'), key: 'totals.costUsd', align: 'end',
    value: (item) => formatUsd(item.totals.costUsd),
  },
])

// Si carica all'apertura, non al montaggio: il dialog vive dentro la scheda del processo e
// interrogare il database ad ogni apertura di scheda sarebbe una query per niente.
watch(() => props.modelValue, async (open) => {
  if (!open || !props.projectId) return

  loading.value = true
  error.value = ''
  report.value = null
  try {
    report.value = await apiCall(`/api/billing/usage/project/${encodeURIComponent(props.projectId)}`, { method: 'GET' })
  } catch (e) {
    error.value = e?.message || t('projects.cost.error')
  } finally {
    loading.value = false
  }
})
</script>
